/**
 * Resource Modal Flow State Machine
 * useReducer-based state management for the multi-step resource collection modal
 */

import { useReducer, useCallback } from 'react';
import type { MaterialSourceType, CEFRLevel, ContentType } from '../../../types/creator-workflow';

// Translation languages supported for course content
export const TRANSLATION_LANGUAGES = [
  { code: 'zh-TW', name: '繁體中文', flag: '🇹🇼' },
  { code: 'zh-CN', name: '简体中文', flag: '🇨🇳' },
  { code: 'ja', name: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: '한국어', flag: '🇰🇷' },
  { code: 'th', name: 'ภาษาไทย', flag: '🇹🇭' },
  { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' },
  { code: 'id', name: 'Bahasa Indonesia', flag: '🇮🇩' },
  { code: 'ms', name: 'Bahasa Melayu', flag: '🇲🇾' },
] as const;

export type ModalStep =
  | 'resource_selection'
  | 'audio_upload'
  | 'english_text'
  | 'translation_text'
  | 'ai_settings'
  | 'processing'
  | 'content_review'
  | 'completed';

export interface AISettings {
  cefrLevel: CEFRLevel;
  contentType: ContentType;
  topic: string;
  scenario?: string;
  targetWordCount: number;
  customRequirements?: string;
}

export interface CollectedData {
  audioFiles?: File[];
  mergedAudioUrl?: string;
  mergedAudioDuration?: number;
  audioRanges?: Array<{ start: number; end: number }>;
  englishText?: string;
  translationText?: string;
  aiSettings?: AISettings;
}

export interface ResourceModalState {
  currentStep: ModalStep;
  selectedResources: { audio: boolean; english: boolean; translation: boolean };
  translationLanguage: string;
  collectedData: CollectedData;
  modalQueue: ModalStep[];
  queueIndex: number;
  materialSource: MaterialSourceType;
  processingStatus: string;
  processingError?: string;
  generatedText?: string;
}

type ResourceModalAction =
  | { type: 'SELECT_RESOURCES'; payload: { audio: boolean; english: boolean; translation: boolean; translationLanguage: string } }
  | { type: 'UPDATE_AUDIO_DATA'; payload: Partial<Pick<CollectedData, 'audioFiles' | 'mergedAudioUrl' | 'mergedAudioDuration' | 'audioRanges'>> }
  | { type: 'SET_ENGLISH_TEXT'; payload: string }
  | { type: 'SET_TRANSLATION_TEXT'; payload: string }
  | { type: 'SET_AI_SETTINGS'; payload: AISettings }
  | { type: 'NEXT_STEP' }
  | { type: 'PREV_STEP' }
  | { type: 'SET_PROCESSING_STATUS'; payload: string }
  | { type: 'SET_PROCESSING_ERROR'; payload: string }
  | { type: 'SET_GENERATED_TEXT'; payload: string }
  | { type: 'COMPLETE' }
  | { type: 'RESET' };

function determineMaterialSource(resources: { audio: boolean; english: boolean; translation: boolean }): MaterialSourceType {
  const { audio, english, translation } = resources;
  if (audio && english && translation) return 'audio_en_translation';
  if (audio && english) return 'audio_en';
  if (audio && translation) return 'audio_translation';
  if (audio) return 'audio_only';
  if (english) return 'text_en';
  if (translation) return 'text_translation';
  return 'ai_generated';
}

function buildModalQueue(resources: { audio: boolean; english: boolean; translation: boolean }): ModalStep[] {
  const queue: ModalStep[] = [];
  const { audio, english, translation } = resources;

  if (!audio && !english && !translation) {
    // AI path: settings → processing → content_review
    queue.push('ai_settings');
  } else {
    if (audio) queue.push('audio_upload');
    if (english) queue.push('english_text');
    if (translation) queue.push('translation_text');
  }
  queue.push('processing');
  return queue;
}

const initialState: ResourceModalState = {
  currentStep: 'resource_selection',
  selectedResources: { audio: false, english: false, translation: false },
  translationLanguage: 'zh-TW',
  collectedData: {},
  modalQueue: [],
  queueIndex: -1,
  materialSource: 'ai_generated',
  processingStatus: '',
};

function reducer(state: ResourceModalState, action: ResourceModalAction): ResourceModalState {
  switch (action.type) {
    case 'SELECT_RESOURCES': {
      const { audio, english, translation, translationLanguage } = action.payload;
      const resources = { audio, english, translation };
      const queue = buildModalQueue(resources);
      return {
        ...state,
        selectedResources: resources,
        translationLanguage,
        materialSource: determineMaterialSource(resources),
        modalQueue: queue,
        queueIndex: 0,
        currentStep: queue[0],
      };
    }

    case 'UPDATE_AUDIO_DATA':
      return {
        ...state,
        collectedData: {
          ...state.collectedData,
          ...action.payload,
        },
      };

    case 'SET_ENGLISH_TEXT':
      return {
        ...state,
        collectedData: { ...state.collectedData, englishText: action.payload },
      };

    case 'SET_TRANSLATION_TEXT':
      return {
        ...state,
        collectedData: { ...state.collectedData, translationText: action.payload },
      };

    case 'SET_AI_SETTINGS':
      return {
        ...state,
        collectedData: { ...state.collectedData, aiSettings: action.payload },
      };

    case 'NEXT_STEP': {
      const nextIndex = state.queueIndex + 1;
      if (nextIndex >= state.modalQueue.length) {
        return { ...state, currentStep: 'completed' };
      }
      return {
        ...state,
        queueIndex: nextIndex,
        currentStep: state.modalQueue[nextIndex],
      };
    }

    case 'PREV_STEP': {
      const prevIndex = state.queueIndex - 1;
      if (prevIndex < 0) {
        return { ...state, currentStep: 'resource_selection', queueIndex: -1 };
      }
      return {
        ...state,
        queueIndex: prevIndex,
        currentStep: state.modalQueue[prevIndex],
      };
    }

    case 'SET_PROCESSING_STATUS':
      return { ...state, processingStatus: action.payload, processingError: undefined };

    case 'SET_PROCESSING_ERROR':
      return { ...state, processingError: action.payload };

    case 'SET_GENERATED_TEXT':
      return { ...state, generatedText: action.payload };

    case 'COMPLETE':
      return { ...state, currentStep: 'completed' };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}

export function useResourceModalFlow() {
  const [state, dispatch] = useReducer(reducer, initialState);

  const selectResources = useCallback((audio: boolean, english: boolean, translation: boolean, translationLanguage: string) => {
    dispatch({ type: 'SELECT_RESOURCES', payload: { audio, english, translation, translationLanguage } });
  }, []);

  const updateAudioData = useCallback(
    (patch: Partial<Pick<CollectedData, 'audioFiles' | 'mergedAudioUrl' | 'mergedAudioDuration' | 'audioRanges'>>) => {
      dispatch({ type: 'UPDATE_AUDIO_DATA', payload: patch });
    },
    [],
  );

  const setEnglishText = useCallback((text: string) => {
    dispatch({ type: 'SET_ENGLISH_TEXT', payload: text });
  }, []);

  const setTranslationText = useCallback((text: string) => {
    dispatch({ type: 'SET_TRANSLATION_TEXT', payload: text });
  }, []);

  const setAISettings = useCallback((settings: AISettings) => {
    dispatch({ type: 'SET_AI_SETTINGS', payload: settings });
  }, []);

  const nextStep = useCallback(() => dispatch({ type: 'NEXT_STEP' }), []);
  const prevStep = useCallback(() => dispatch({ type: 'PREV_STEP' }), []);
  const complete = useCallback(() => dispatch({ type: 'COMPLETE' }), []);
  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  const setProcessingStatus = useCallback((status: string) => {
    dispatch({ type: 'SET_PROCESSING_STATUS', payload: status });
  }, []);

  const setProcessingError = useCallback((error: string) => {
    dispatch({ type: 'SET_PROCESSING_ERROR', payload: error });
  }, []);

  const setGeneratedText = useCallback((text: string) => {
    dispatch({ type: 'SET_GENERATED_TEXT', payload: text });
  }, []);

  return {
    state,
    selectResources,
    updateAudioData,
    setEnglishText,
    setTranslationText,
    setAISettings,
    nextStep,
    prevStep,
    complete,
    reset,
    setProcessingStatus,
    setProcessingError,
    setGeneratedText,
  };
}
